import { Box, Chip, Tooltip } from "@mui/material";
import { blue, green, grey, orange, red } from "@mui/material/colors";
import { memo, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import FlxGrid from "../../../../../_src_shared/components/layout/FlxGrid";
import CardPaper from "../../../../../components/ui/CardPaper";
import PropertyMap from "../../../../../components/common/PropertyMap";
import { filterAssociationFields } from "../../../../../components/associations/config/associationFieldsConfig";
import { useUnderwritingHook } from "../../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../../redux/actions/loanDrilldownActions";
import RecordDocCoveragePanel from "../../../shared/RecordDocCoveragePanel";

import {
  AddOutlined,
  CheckCircleOutline,
  ChevronRight,
  HomeWorkOutlined,
  HourglassEmpty,
  ReportProblemOutlined,
  WaterOutlined,
} from "@mui/icons-material";
import useAssociationUtils from "../../../../../components/associations/hooks/useAssociationUtils";
import { isDocHidden } from "../../../../../_src_shared/utils/functions/isDocHidden";
import { over } from "lodash";
import {
  annualOrDerive,
  CONDITION_TONE,
  FLOOD_TONE_STYLES,
  FONT_MONO,
  FONT_SERIF,
  fmtDateShort,
  fmtMoney,
  fmtNumber,
  getFloodCertChipState,
  SHADOW_LG,
  SHADOW_MD,
  SubTag,
  TOKENS,
} from "./propertyCardShared";
import { addressLabel } from "../../../../../_src_shared/utils/functions/addressLabel";
import { teal } from "../../../../../theme/colors";
import {
  PropertyMarketSection,
  PropertyUnitsSection,
} from "./PropertyCardSections";

// The property documents panel's own height cap (Applicants use 640).
const PROPERTY_DOC_PANEL_MAX_H = 320;

const BORDER_COLOR = "#d8e0ea";
const BORDER = `1px solid ${BORDER_COLOR}`;


const CHIP_MAX_WIDTH = 180;

// Per-type associations renderer — one row per type that actually has items.
// Types with empty arrays are skipped entirely so the card never shows
// "Borrowers: (none)" rows. The "+ Add" affordance lives in the section
// header (rendered by the parent), not here, so this stays purely display.
const PerTypeAssociations = ({ loan, associations, fields }) => {
  const enabled = useMemo(() => filterAssociationFields(fields), [fields]);
  const groups = useMemo(() => {
    const out = [];
    for (const field of enabled) {
      const ids = associations?.[field.key] || [];
      if (!ids.length) continue;
      const source = field.getSource(loan) || [];
      const records = ids
        .map((id) => source.find((r) => r?._id === id))
        .filter(Boolean);
      if (records.length === 0) continue;
      out.push({ field, records });
    }
    return out;
  }, [enabled, associations, loan]);

  if (groups.length === 0) return null;

  return (
    <Flx column g={1}>
      {groups.map(({ field, records }) => {
        const Icon = field.icon;
        return (
          <Flx key={field.key} ac g={1} sx={{ minWidth: 0 }}>
            <Flx
              ac
              jc="center"
              sx={{
                width: 22,
                height: 22,
                borderRadius: 0.75,
                bgcolor: `${field.color}1A`,
                color: field.color,
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: "13px !important" }} />
            </Flx>
            <Txt
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                color: grey[700],
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                width: 78,
                flexShrink: 0,
              }}
            >
              {field.label}
            </Txt>
            <Flx wrap g={0.5} sx={{ flex: 1, minWidth: 0 }}>
              {records.map((rec) => {
                const label = field.getLabel(rec);
                return (
                  <Tooltip key={`${field.key}-${rec._id}`} title={label} arrow>
                    <Chip
                      size="small"
                      label={label}
                      sx={{
                        height: 22,
                        maxWidth: CHIP_MAX_WIDTH,
                        bgcolor: `${field.color}14`,
                        border: `1px solid ${field.color}40`,
                        color: grey[900],
                        fontSize: "10.5px !important",
                        fontWeight: 500,
                        "& .MuiChip-label": {
                          px: 0.75,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Flx>
          </Flx>
        );
      })}
    </Flx>
  );
};

/**
 * PropertyTagStrip
 *
 * Single horizontal flex-wrap row that consolidates everything that used
 * to live in 4 separate bordered sections (condition rating + status,
 * proposed-type, warnings, flood-cert). Each chip self-classifies via a
 * tone (good / info / warn / urgent / muted) so flags pop visually
 * without needing prose labels. Renders nothing when there's nothing to
 * show — keeps the card tight on properties with few flags.
 */
const TAG_TONES = {
  good: { fg: green[800], bg: green[50], border: green[100] },
  info: { fg: blue[800], bg: blue[50], border: blue[100] },
  warn: { fg: orange[800], bg: orange[50], border: orange[100] },
  urgent: { fg: red[700], bg: red[50], border: red[100] },
  muted: { fg: grey[700], bg: grey[100], border: grey[200] },
};

const Tag = ({ tone = "muted", icon, label, tooltip }) => {
  const t = TAG_TONES[tone] || TAG_TONES.muted;
  const chip = (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: 0.85,
        py: 0.3,
        borderRadius: "6px",
        bgcolor: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.02em",
        maxWidth: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        cursor: tooltip ? "help" : "default",
      }}
    >
      {icon}
      <span>{label}</span>
    </Box>
  );
  if (!tooltip) return chip;
  return (
    <Tooltip title={tooltip} placement="top" arrow>
      {chip}
    </Tooltip>
  );
};

const PropertyTagStrip = ({
  condition,
  conditionStatuses,
  proposedType,
  propertyType,
  floodZone,
  changeOfUse,
  listedForSale,
  floodCertResult,
  floodCertDate,
}) => {
  // Build the tag list lazily so the strip only renders when there's at
  // least one flag worth showing.
  const tags = [];

  if (condition) {
    const tone = CONDITION_TONE[condition];
    tags.push({
      key: `c-${condition}`,
      label: condition,
      tooltip: "Property condition rating at origination (C1–C6)",
      // Condition uses its own per-rating palette; map onto Tag's bg/fg
      // by overriding the chip via a custom render.
      override: tone ? { bg: tone.bg, fg: tone.fg, border: tone.bg } : null,
    });
  }
  const visibleStatuses = (conditionStatuses || []).slice(0, 2);
  visibleStatuses.forEach((s) => {
    tags.push({ key: `s-${s}`, label: s, tone: "muted" });
  });
  if ((conditionStatuses || []).length > visibleStatuses.length) {
    const overflow = conditionStatuses.slice(visibleStatuses.length);
    tags.push({
      key: "s-more",
      label: `+${overflow.length} more`,
      tone: "muted",
      tooltip: overflow.join("\n"),
    });
  }
  if (proposedType && proposedType !== propertyType) {
    tags.push({
      key: "proposed",
      label: `Proposed: ${proposedType}`,
      tone: "info",
    });
  }
  if (floodZone) {
    tags.push({
      key: "flood-zone",
      label: "Flood zone",
      tone: "info",
      icon: <WaterOutlined sx={{ fontSize: 12 }} />,
    });
  }
  if (changeOfUse) {
    tags.push({
      key: "change-of-use",
      label: "Change of use",
      tone: "warn",
      icon: <ReportProblemOutlined sx={{ fontSize: 12 }} />,
    });
  }
  if (listedForSale) {
    tags.push({
      key: "listed",
      label: "Listed for sale",
      tone: "urgent",
      icon: <ReportProblemOutlined sx={{ fontSize: 12 }} />,
    });
  }
  // Flood-cert chip — single-line state with the full breakdown surfaced
  // via tooltip. Replaces the old multi-row FloodCertDetailsArea.
  const floodCertChip =
    floodCertResult || floodCertDate ? floodCertResult : null;
  if (floodCertChip || floodCertResult === null) {
    const { tone: certTone, label: certLabel } =
      getFloodCertChipState(floodCertResult);
    const tooltipLines = [];
    if (floodCertResult?.floodZone)
      tooltipLines.push(`Zone: ${floodCertResult.floodZone}`);
    if (floodCertResult?.inSpecialFloodHazardArea === true)
      tooltipLines.push("In SFHA: Yes");
    else if (floodCertResult?.inSpecialFloodHazardArea === false)
      tooltipLines.push("In SFHA: No");
    if (floodCertResult?.nfipCommunityName)
      tooltipLines.push(`Community: ${floodCertResult.nfipCommunityName}`);
    if (floodCertResult?.nfipPanelId)
      tooltipLines.push(`Panel: ${floodCertResult.nfipPanelId}`);
    if (floodCertResult?.followUp?.userMessage)
      tooltipLines.push(floodCertResult.followUp.userMessage);
    // Skip when there's truly nothing to say — i.e. no result + no date.
    if (floodCertResult || floodCertDate) {
      const tagToneMap = {
        success: "good",
        pending: "info",
        warning: "warn",
        error: "urgent",
        neutral: "muted",
      };
      tags.push({
        key: "flood-cert",
        label: certLabel,
        tone: tagToneMap[certTone] || "muted",
        tooltip: tooltipLines.length ? tooltipLines.join("\n") : null,
      });
    }
  }

  if (tags.length === 0) return null;

  return (
    <Flx
      ac
      wrap
      g={0.6}
      sx={{
        mt: 2,
      }}
    >
      {tags.map((t) =>
        t.override ? (
          <Tooltip
            key={t.key}
            title={t.tooltip || ""}
            placement="top"
            arrow
            disableHoverListener={!t.tooltip}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                px: 0.85,
                py: 0.3,
                borderRadius: "6px",
                bgcolor: t.override.bg,
                color: t.override.fg,
                border: `1px solid ${t.override.border}`,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.02em",
              }}
            >
              {t.label}
            </Box>
          </Tooltip>
        ) : (
          <Tag
            key={t.key}
            tone={t.tone}
            icon={t.icon}
            label={t.label}
            tooltip={t.tooltip}
          />
        ),
      )}
    </Flx>
  );
};

const PropertyCard = memo(({ property, isSelected, onClick, onEditUnits, onAddUnits, market }) => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanId = loanDrilldown?._id;
  const { updateSubjectProperty } = useUnderwritingHook();
  const { getAssociationDocumentsByPropertyId } = useAssociationUtils();
  const propertyFiles = useMemo(
    () =>
      (
        getAssociationDocumentsByPropertyId(loanDrilldown, property?._id) || []
      ).filter((f) => !isDocHidden(f)),
    [getAssociationDocumentsByPropertyId, loanDrilldown, property?._id],
  );

  // Track when the embedded map is expanded so we can switch the card from
  // map-on-the-left to map-stacked-on-top, giving the bigger map full width.
  const [mapExpanded, setMapExpanded] = useState(false);

  // Persist a pin-drop from the embedded map. Overwrites the address's
  // latitude/longitude with the user-selected coords and stamps
  // geocodeQuality: "pin" (manualEntry stays true so the audit trail
  // survives — the chip on the card just shifts from "ZIP-level" to
  // "pinned"). Returns a Promise so PropertyMap can render its loading
  // spinner until the persistence settles.
  const handlePinDrop = useCallback(
    ({ latitude, longitude }) => {
      if (!loanId || !property?._id) return Promise.resolve();
      const nextAddress = {
        ...(property?.address || {}),
        latitude,
        longitude,
        coordinateTuple: [latitude, longitude],
        geocodeQuality: "pin",
        // Recorded so FloodCertStatusBlock can flag a stale cert when the
        // user pins after a flood cert has already been pulled.
        geocodePinnedAt: new Date().toISOString(),
        manualEntry: true,
      };
      return new Promise((resolve) => {
        updateSubjectProperty({
          loanId,
          propertyId: property._id,
          data: { address: nextAddress },
          onSuccessFn: (updatedLoan) => {
            dispatch(loanDrilldownSet(updatedLoan));
            resolve();
          },
          onFailFn: () => resolve(),
          onCompleteFn: () => resolve(),
        });
      });
    },
    [loanId, property?._id, property?.address, updateSubjectProperty, dispatch],
  );

  // Hooks below this point need to run on every render — they're declared
  // before the `if (!property) return null` early-return so React's
  // hook-order invariant doesn't trip when a property is briefly missing.
  const arvDelta = useMemo(() => {
    const arvNum = Number(property?.arv);
    const asIsNum = Number(property?.asIsValue);
    if (!Number.isFinite(arvNum) || !Number.isFinite(asIsNum) || asIsNum <= 0) {
      return null;
    }
    const pct = ((arvNum - asIsNum) / asIsNum) * 100;
    if (!Number.isFinite(pct) || pct === 0) return null;
    return `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`;
  }, [property?.arv, property?.asIsValue]);

  const subTags = useMemo(() => {
    const items = [];
    if (property?.propertyType)
      items.push({ key: "type", label: property.propertyType, tone: "muted" });
    items.push({ key: "subject", label: "Subject property", tone: "muted" });
    if (
      property?.proposedPropertyType &&
      property.proposedPropertyType !== property?.propertyType
    ) {
      items.push({
        key: "proposed",
        label: `Proposed: ${property.proposedPropertyType}`,
        tone: "info",
      });
    }
    if (property?.address?.manualEntry) {
      items.push({
        key: "manual-entry",
        label:
          property?.address?.geocodeQuality === "pin"
            ? "Manual address · pinned"
            : "Manual address · ZIP-level",
        tone: "warn",
      });
    }
    if (property?.floodZone)
      items.push({ key: "flood-zone", label: "Flood zone", tone: "info" });
    if (property?.changeOfUseFlag)
      items.push({
        key: "change-of-use",
        label: "Change of use",
        tone: "warn",
      });
    if (property?.propertyListedForSale)
      items.push({
        key: "listed",
        label: "Listed for sale",
        tone: "urgent",
      });
    return items;
  }, [
    property?.propertyType,
    property?.proposedPropertyType,
    property?.address?.manualEntry,
    property?.address?.geocodeQuality,
    property?.floodZone,
    property?.changeOfUseFlag,
    property?.propertyListedForSale,
  ]);

  if (!property) return null;

  const fullAddress = addressLabel(property?.address);
  const city = property?.address?.city;
  const state = property?.address?.state;
  const propertyType = property?.propertyType;
  const proposedType = property?.proposedPropertyType;
  const asIsValue = property?.asIsValue;
  const appraisalDate = fmtDateShort(property?.appraisalDate);
  const arv = property?.arv;
  const purchasePrice = property?.purchasePrice;
  const purchaseDate = fmtDateShort(property?.purchaseDate);
  const costBasis = property?.costBasis;
  const residentialSqFt = property?.residentialSqFootage;
  const condition = property?.propertyConditionAtOrigination;
  const conditionStatuses = Array.isArray(property?.propertyStatusAtOrigination)
    ? property.propertyStatusAtOrigination.filter(Boolean)
    : [];
  const constructionBudget = Number(property?.constructionBudget) || 0;
  const financedBudgetPct = Number(property?.financedBudgetPercent) || 0;
  const annualTaxes = annualOrDerive(
    property?.annualTaxes,
    property?.monthlyTaxes,
  );
  const annualInsurance = annualOrDerive(
    property?.annualInsurance,
    property?.monthlyInsurance,
  );
  const annualHoaFees = annualOrDerive(
    property?.annualHoaFees,
    property?.monthlyHoaFees,
  );
  const annualFloodInsurance = annualOrDerive(
    property?.annualFloodInsurance,
    property?.monthlyFloodInsurance,
  );

  // Product-type drives which stat blocks render. Once shown, individual
  // values render even when zero (so a zeroed-out DSCR HOA still surfaces
  // as $0 rather than silently disappearing). Empty-data hiding is reserved
  // for products where the field doesn't apply at all.
  const productType = (loanDrilldown?.loanProductType || "").toLowerCase();
  const isDscrProduct =
    productType.includes("dscr") || productType.includes("rental");
  const isConstructionProduct =
    productType.includes("fix") ||
    productType.includes("flip") ||
    productType.includes("ground up") ||
    productType.includes("construction") ||
    productType.includes("rehab");
  const floodZone = Boolean(property?.floodZone);
  const changeOfUse = Boolean(property?.changeOfUseFlag);
  const listedForSale = Boolean(property?.propertyListedForSale);

  const { streetNumber, streetName, street } = property?.address || {};
  const baseStreetLine =
    streetNumber && streetName
      ? `${streetNumber} ${streetName}`
      : street ||
        (fullAddress ? fullAddress.split(",")[0].trim() : null) ||
        "No address";
  // Unit/suite (e.g. "#734") is stored separately and NOT baked into
  // fullAddress, so append it to the street line so the card shows the full unit
  // address. Stored verbatim (already carries its own "#"/"Unit" prefix).
  const unitNumber = property?.address?.addressUnitNumber;
  const streetLine =
    unitNumber && baseStreetLine !== "No address"
      ? `${baseStreetLine} ${unitNumber}`
      : baseStreetLine;
  const cityStateLine = [city, state].filter(Boolean).join(", ");

  const hasCoords =
    property?.address?.latitude != null && property?.address?.longitude != null;

  const fullAddressText = [cityStateLine, property?.address?.zip]
    .filter(Boolean)
    .join(" ");

  return (
    <CardPaper
      selected={isSelected}
      onClick={onClick}
      sx={{
        position: "relative",
        border: `1px solid ${TOKENS.line}`,
        boxShadow: SHADOW_MD,
        cursor: "pointer",
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
        // Layout switches between two row/area configurations so that the map
        // can either ride alongside the files cell (default) or drop to a
        // full-width row underneath the content (when expanded). Keeping
        // PropertyMap mounted in a single grid area across both states
        // preserves its internal isExpanded/zoom/snapshot state.
        gridTemplateAreas: {
          xs: hasCoords
            ? mapExpanded
              ? `"map" "left" "files"`
              : `"left" "map" "files"`
            : `"left" "files"`,
          md: hasCoords
            ? mapExpanded
              ? `"map map" "left files"`
              : `"left map" "left files"`
            : `"left files"`,
        },
        gridTemplateRows: {
          md: hasCoords ? (mapExpanded ? "auto auto" : "auto 1fr") : "auto",
        },
        transition:
          "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          boxShadow: SHADOW_LG,
          borderColor: TOKENS.ink4,
          transform: "translateY(-2px)",
          // The theme's primary-button hover (teal-700) — same as ApplicantCard.
          "& .open-indicator": {
            background: teal[700],
            borderColor: teal[700],
          },
          "& .open-indicator-arrow": {
            transform: "translateX(2px)",
          },
        },
      }}
    >
      {/* ═══════════════════════════════════════════
          LEFT COLUMN — header + figures + associated + status block
          ═══════════════════════════════════════════ */}
      <Box
        sx={{
          gridArea: "left",
          p: { xs: "22px", md: "28px 32px" },
          display: "flex",
          flexDirection: "column",
          gap: 3,
          borderRight: { md: `1px solid ${TOKENS.line}` },
          borderBottom: { xs: `1px solid ${TOKENS.line}`, md: "none" },
          minWidth: 0,
        }}
      >
        {/* HEADER */}
        <Flx
          g={2.5}
          sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
        >
          <Flx g={1.75} sx={{ alignItems: "flex-start", flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "10px",
                background: `linear-gradient(135deg, ${TOKENS.surface2} 0%, ${TOKENS.accentSoft} 100%)`,
                border: `1px solid ${TOKENS.line}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <HomeWorkOutlined sx={{ fontSize: 22, color: TOKENS.accent }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Txt
                component="h2"
                sx={{
                  fontFamily: FONT_SERIF,
                  fontWeight: 500,
                  fontSize: 24,
                  letterSpacing: "-0.02em",
                  m: 0,
                  mb: "4px",
                  color: TOKENS.ink,
                  lineHeight: 1.15,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {streetLine}
              </Txt>
              <Flx ac wrap g={1} sx={{ minWidth: 0 }}>
                {fullAddressText && (
                  <Txt sx={{ fontSize: 13, color: TOKENS.ink3 }}>
                    {fullAddressText}
                  </Txt>
                )}
                {subTags.length > 0 && (
                  <>
                    <Box
                      sx={{
                        width: 3,
                        height: 3,
                        borderRadius: "50%",
                        background: TOKENS.ink4,
                      }}
                    />
                    {subTags.map((t) => (
                      <SubTag key={t.key} tone={t.tone}>
                        {t.label}
                      </SubTag>
                    ))}
                  </>
                )}
              </Flx>
            </Box>
          </Flx>

          {/* OPEN INDICATOR — `View details →` mono pill that color-shifts
              and the arrow nudges right when the card is hovered. */}
          <Flx
            ac
            g={0.75}
            className="open-indicator"
            sx={{
              flexShrink: 0,
              px: "10px",
              pl: "12px",
              py: "6px",
              borderRadius: "6px",
              // Same colours as the theme's default <Button>: teal-600 fill,
              // white label (colors.js — teal-500 never carries white text).
              border: `1px solid ${teal[600]}`,
              background: teal[600],
              color: "#fff",
              fontSize: 11,
              fontFamily: FONT_MONO,
              fontWeight: 500,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              transition: "all 0.2s",
              height: "fit-content",
            }}
          >
            <span>View details</span>
            <Box
              component="span"
              className="open-indicator-arrow"
              sx={{ display: "inline-block", transition: "transform 0.2s" }}
            >
              →
            </Box>
          </Flx>
        </Flx>

        {/* PRIMARY FIGURES — 4-cell connected box. ARV cell highlights
            green when there's an as-is delta to show the value-add story. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              // ARV is construction-only; DSCR drops it to a 3-cell row.
              sm: isConstructionProduct ? "repeat(4, 1fr)" : "repeat(3, 1fr)",
            },
            background: TOKENS.surface3,
            border: `1px solid ${TOKENS.line}`,
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <FigureCell label="As-is value" value={fmtMoney(asIsValue)} idx={0} />
          {isConstructionProduct && (
            <FigureCell
              label="ARV"
              value={fmtMoney(arv)}
              delta={arvDelta}
              highlight={!!arvDelta && !arvDelta.startsWith("-")}
              idx={1}
            />
          )}
          <FigureCell
            label="Purchase price"
            value={fmtMoney(purchasePrice)}
            idx={isConstructionProduct ? 2 : 1}
          />
          <FigureCell
            label="Cost basis"
            value={fmtMoney(costBasis)}
            idx={isConstructionProduct ? 3 : 2}
          />
        </Box>

        {/* SECONDARY FIGURES — sparse 3-cell row. Construction-only cells
            only render when the loan is a construction product AND the
            value is actually set. */}
        <Box
          sx={{
            mt: 1.5,
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
            },
            gap: 3,
            pl: 0.5,
          }}
        >
          <FigureSecondary
            label="Square footage"
            value={
              residentialSqFt ? (
                <>
                  {Number(residentialSqFt).toLocaleString()}
                  <Box
                    component="span"
                    sx={{
                      color: TOKENS.ink3,
                      fontSize: 12,
                      ml: "4px",
                      fontFamily: "inherit",
                    }}
                  >
                    sq ft
                  </Box>
                </>
              ) : (
                "—"
              )
            }
          />
          {isConstructionProduct && (
            <FigureSecondary
              label="Construction budget"
              value={fmtMoney(constructionBudget)}
            />
          )}
          {isConstructionProduct && (
            <FigureSecondary
              label="Financed"
              value={
                Number(financedBudgetPct) > 0
                  ? fmtNumber(
                      financedBudgetPct > 1
                        ? financedBudgetPct
                        : financedBudgetPct * 100,
                      "%",
                    )
                  : "—"
              }
              muted={!Number(financedBudgetPct)}
            />
          )}
          {isDscrProduct && (
            <>
              <FigureSecondary
                label="Taxes / yr"
                value={fmtMoney(annualTaxes ?? 0)}
              />
              <FigureSecondary
                label="Insurance / yr"
                value={fmtMoney(annualInsurance ?? 0)}
              />
            </>
          )}
        </Box>

        {/* NOTES — property-level free-form notes, only when there are any. Sits above Market data / Units.
            Read-only here; editing lives in the sidebar's Details > Notes
            section. No stopPropagation — clicking the notes area should open
            the card like the rest of the surface. */}
        {property?.subjectPropertyNotes ? (
          <Box sx={{ mt: 4 }}>
            <Txt
              sx={{
                display: "block",
                mb: 1.25,
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: TOKENS.ink3,
                fontWeight: 500,
              }}
            >
              Notes
            </Txt>
            <Txt
              sx={{
                display: "block",
                fontSize: 12.5,
                color: TOKENS.ink2,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {property.subjectPropertyNotes}
            </Txt>
          </Box>
        ) : null}

        {/* FOLD-ABLE SECTIONS — Market data, then Units when the property has
            any; both start folded, the same header as the Applicants card.
            (The Associated area was removed from this card.) */}
        <Flx column g={3} sx={{ mt: 4 }}>
          <PropertyMarketSection property={property} market={market} />
          <PropertyUnitsSection
            units={property?.units}
            onEditUnits={onEditUnits}
            onAddUnits={onAddUnits}
          />
        </Flx>

        {/* FLOOD-CERT STATUS — pinned to the bottom of the left column
            so it always sits above the right column's documents footer.
            Tone (good / warn / urgent / info / muted) is derived from
            lastFloodCertResult.status. */}
        <Box sx={{ mt: "auto" }}>
          <FloodCertStatusBlock
            result={property?.lastFloodCertResult}
            floodCertDate={property?.floodCertDate}
            address={property?.address}
          />
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════
          MAP CELL — sits in column 2 row 1 by default, but moves to a
          full-width row 2 when expanded. PropertyMap stays mounted across
          both states so internal state (isExpanded, snapshots, zoom)
          carries over without re-mounting.
          ═══════════════════════════════════════════ */}
      {hasCoords && (
        <Box
          sx={{
            gridArea: "map",
            background: TOKENS.surface3,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            // Fixed pixel height when collapsed so the cell never expands
            // to absorb leftover grid space (which is what made the map
            // bloat on cards with no documents). Auto when expanded so
            // the inner panel's 560px can drive the cell.
            height: mapExpanded ? "auto" : 300,
            // Map sits on top in both states, so its bottom edge is the
            // one that needs a separator from the row(s) underneath.
            borderBottom: BORDER,
          }}
        >
          <PropertyMapPanel
            property={property}
            mapExpanded={mapExpanded}
            setMapExpanded={setMapExpanded}
            onPinDrop={handlePinDrop}
          />
        </Box>
      )}

      {/* FILES CELL — column 2 row 2 by default; column 2 row 1 when the
          map is expanded. */}
      <Box
        sx={{
          gridArea: "files",
          background: TOKENS.surface,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          position: "relative",
        }}
      >
        {/* Same documents rail as the Applicants tab. Trial: DocGroup
            Coverage view of the property's documents. To go back, render
            RecordDocumentsPanel (shared/) with the same props. */}
        <RecordDocCoveragePanel
          assignTo={{
            type: "property",
            id: property?._id,
            label:
              addressLabel(property?.address) ||
              property?.stringAddressFinalTitle ||
              "Property",
          }}
          files={propertyFiles}
          linkedDocumentIds={property?.associations?.loanDocuments || []}
          // Its OWN content may only push the card to 320px; past that it
          // scrolls (top / bottom fades). But it always fills the cell, so
          // when the left column makes the card taller it grows with it.
          //   maxHeight 320 — caps what the panel contributes to the row;
          //   minHeight 100% — fills the stretched cell; min-height beats
          //   max-height, and while the row is being sized the percentage
          //   counts as auto, so it can't inflate the row itself.
          // (flex-basis can't do this: it sizes along the column but still
          // lets the content set the row's height.)
          sx={{ maxHeight: PROPERTY_DOC_PANEL_MAX_H, minHeight: "100%" }}
        />
      </Box>
    </CardPaper>
  );
});

/* ═══════════════════════════════════════════════════════════
   PROPERTY CARD HELPERS
   ═══════════════════════════════════════════════════════════ */

// Cell in the primary 4-column figures bar. Shares right-borders with
// neighbours via the `idx` prop; the highlight prop tints the value
// green and surfaces the +N% delta line for the ARV cell.
const FigureCell = ({ label, value, delta, highlight, idx }) => (
  <Box
    sx={{
      p: "14px 16px",
      borderRight: {
        xs: idx % 2 === 0 ? `1px solid ${TOKENS.line}` : "none",
        sm: idx < 3 ? `1px solid ${TOKENS.line}` : "none",
      },
      borderBottom: {
        xs: idx < 2 ? `1px solid ${TOKENS.line}` : "none",
        sm: "none",
      },
      minWidth: 0,
    }}
  >
    <Txt
      sx={{
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: TOKENS.ink3,
        fontWeight: 500,
        mb: "4px",
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontFamily: FONT_MONO,
        fontSize: 16,
        fontWeight: 500,
        color: highlight ? TOKENS.good : TOKENS.ink,
        letterSpacing: "-0.01em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {value}
    </Txt>
    {delta && (
      <Txt
        sx={{
          fontSize: 10.5,
          color: TOKENS.good,
          mt: "2px",
          fontFamily: FONT_MONO,
        }}
      >
        {delta}
      </Txt>
    )}
  </Box>
);

// Sparse secondary figure cell — no borders, smaller value font.
const FigureSecondary = ({ label, value, muted }) => (
  <Box>
    <Txt
      sx={{
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: TOKENS.ink3,
        fontWeight: 500,
        mb: "4px",
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontFamily: FONT_MONO,
        fontSize: 14,
        fontWeight: 500,
        color: muted ? TOKENS.ink3 : TOKENS.ink,
        letterSpacing: "-0.01em",
      }}
    >
      {value}
    </Txt>
  </Box>
);

// Maps the lastFloodCertResult.status to the design's status-block tone.
// Returns the colored block for the bottom of the left column. The chip
// state helper is reused so wording stays consistent with the smaller
// chip variant elsewhere.
const FLOOD_BLOCK_TONES = {
  good: {
    bg: TOKENS.goodSoft,
    border: TOKENS.goodLine,
    iconBg: TOKENS.good,
    actionFg: TOKENS.good,
    actionBorder: "rgba(45,106,74,0.2)",
  },
  warn: {
    bg: TOKENS.warnSoft,
    border: TOKENS.warnLine,
    iconBg: TOKENS.warn,
    actionFg: TOKENS.warn,
    actionBorder: "rgba(184,100,29,0.2)",
  },
  urgent: {
    bg: TOKENS.urgentSoft,
    border: "#e8c4c4",
    iconBg: TOKENS.urgent,
    actionFg: TOKENS.urgent,
    actionBorder: "rgba(168,42,42,0.2)",
  },
  info: {
    bg: TOKENS.infoSoft,
    border: TOKENS.infoLine,
    iconBg: TOKENS.info,
    actionFg: TOKENS.info,
    actionBorder: "rgba(44,82,130,0.2)",
  },
  muted: {
    bg: TOKENS.surface2,
    border: TOKENS.line,
    iconBg: TOKENS.ink3,
    actionFg: TOKENS.ink2,
    actionBorder: TOKENS.line,
  },
};

const FloodCertStatusBlock = ({ result, floodCertDate, address }) => {
  // TEMP: only surface the flood-cert block when a cert result actually
  // exists. Without one, render nothing rather than the
  // "Flood certification — not ordered" prompt. Remove this early return
  // to bring the not-ordered state back.
  if (!result) return null;

  const { tone: chipTone, label } = getFloodCertChipState(result);
  // Map the chip's tones onto the block's design palette.
  const blockToneMap = {
    success: "good",
    pending: "info",
    warning: "warn",
    error: "urgent",
    neutral: "muted",
  };
  const blockTone = blockToneMap[chipTone] || "muted";
  const t = FLOOD_BLOCK_TONES[blockTone];
  const ordered = !!result;
  const title = ordered
    ? `Flood certification — ${label.replace(/^Flood cert: /, "")}`
    : "Flood certification — not ordered";

  // Build the meta line (mono, secondary). Pulled-at date + by-user when
  // we have it, otherwise the floodCertDate as a fallback. Empty meta
  // still produces a clean block — the title alone is informative.
  const pulledAt = result?.pulledAt || floodCertDate;
  const formattedPulled = pulledAt ? fmtDateShort(pulledAt) : null;
  const metaParts = [];
  if (formattedPulled) metaParts.push(`Pulled ${formattedPulled}`);
  if (result?.pulledBy) metaParts.push(`by ${result.pulledBy}`);
  if (result?.floodCertificationId)
    metaParts.push(`req #${result.floodCertificationId}`);
  if (!ordered) metaParts.push("Required before closing");
  const meta = metaParts.join(" · ");

  // If the user pinned a precise location AFTER the cert was pulled, the
  // existing cert was based on stale (ZIP-centroid) coords. Surface that
  // as a small inline hint — the user decides whether to re-order.
  const pinnedAt = address?.geocodePinnedAt;
  const certBasedOnStaleCoords = Boolean(
    ordered && pulledAt && pinnedAt && new Date(pinnedAt) > new Date(pulledAt),
  );

  return (
    <Box
      sx={{
        background: t.bg,
        border: `1px solid ${t.border}`,
        borderRadius: "10px",
        p: "14px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Flx ac g={1.5} sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            background: t.iconBg,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {blockTone === "good" ? (
            <CheckCircleOutline sx={{ fontSize: 18 }} />
          ) : blockTone === "warn" || blockTone === "urgent" ? (
            <ReportProblemOutlined sx={{ fontSize: 16 }} />
          ) : blockTone === "info" ? (
            <HourglassEmpty sx={{ fontSize: 16 }} />
          ) : (
            <WaterOutlined sx={{ fontSize: 16 }} />
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Txt
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: TOKENS.ink,
              mb: "1px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Txt>
          {meta && (
            <Txt
              sx={{
                fontSize: 11.5,
                color: TOKENS.ink2,
                fontFamily: FONT_MONO,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {meta}
            </Txt>
          )}
          {certBasedOnStaleCoords && (
            <Txt
              sx={{
                fontSize: 11,
                color: TOKENS.warn,
                fontWeight: 600,
                mt: "2px",
                whiteSpace: "normal",
              }}
            >
              Location pinned after this cert — re-order recommended.
            </Txt>
          )}
        </Box>
      </Flx>
      <Box
        sx={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: ordered ? t.actionFg : "#fff",
          background: ordered ? "rgba(255,255,255,0.6)" : t.iconBg,
          fontWeight: 500,
          fontFamily: FONT_MONO,
          px: "10px",
          py: "5px",
          borderRadius: "4px",
          border: `1px solid ${ordered ? t.actionBorder : t.iconBg}`,
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        {ordered ? "View cert" : "Order now"}
      </Box>
    </Box>
  );
};

/**
 * PropertyMapPanel
 *
 * Thin wrapper around PropertyMap that fills its grid cell. When collapsed
 * the cell row is `minmax(220px, 1fr)`, so `height: "100%"` resolves to a
 * concrete height the embedded panels can latch onto; when expanded the
 * cell is `auto` and the inner panel's `560px` height drives the cell.
 */
const PropertyMapPanel = ({
  property,
  mapExpanded,
  setMapExpanded,
  onPinDrop,
}) => (
  <PropertyMap
    address={property?.address}
    property={property}
    height={mapExpanded ? "560px" : "100%"}
    expandedHeight="560px"
    expandable
    onExpandChange={setMapExpanded}
    zoom={12}
    compact
    onPinDrop={onPinDrop}
    sx={{
      height: "100%",
      width: "100%",
      borderRadius: 0,
      border: "none",
    }}
  />
);

const FloodDetail = ({ label, value }) => (
  <Flx column g={0.25} sx={{ minWidth: 0 }}>
    <Txt
      sx={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: grey[500],
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: grey[800],
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value || "—"}
    </Txt>
  </Flx>
);

// eslint-disable-next-line no-unused-vars
const FloodCertDetailsArea = ({ result, floodCertDate }) => {
  const { tone, icon: Icon, label } = getFloodCertChipState(result);
  const styles = FLOOD_TONE_STYLES[tone];

  const pulledAt = fmtDateShort(result?.pulledAt || floodCertDate);
  const determinationDate = fmtDateShort(result?.determinationDate);
  const mapDate = fmtDateShort(result?.mapDate);
  const panelDate = fmtDateShort(result?.nfipPanelDate);

  const sfhaValue =
    result?.inSpecialFloodHazardArea === true
      ? "Yes"
      : result?.inSpecialFloodHazardArea === false
        ? "No"
        : null;

  const hasAnyDetails =
    result?.floodZone ||
    sfhaValue ||
    result?.nfipCommunityName ||
    result?.nfipCommunityId ||
    result?.nfipPanelId ||
    panelDate ||
    mapDate ||
    determinationDate ||
    result?.floodCertificationId;

  const followUpMessage =
    result?.followUp?.userMessage || result?.reason || null;
  const vendorMessages = Array.isArray(result?.vendorMessages)
    ? result.vendorMessages.filter(Boolean)
    : [];

  return (
    <Flx
      column
      g={1}
      sx={{
        mt: 1.5,
        pt: 1.5,
        borderTop: `1px solid ${grey[100]}`,
      }}
    >
      <Flx ac jb wrap g={1}>
        <Flx ac g={0.75}>
          <WaterOutlined sx={{ fontSize: 14, color: grey[500] }} />
          <Txt
            sx={{
              fontSize: 10.5,
              letterSpacing: "0.03em",
              textTransform: "uppercase",
              color: grey[500],
              fontWeight: 600,
            }}
          >
            Flood Certification
          </Txt>
        </Flx>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 0.9,
            py: 0.4,
            borderRadius: "6px",
            background: styles.bg,
            border: `1px solid ${styles.border}`,
          }}
        >
          <Icon sx={{ fontSize: 14, color: styles.fg }} />
          <Txt sx={{ fontSize: 11.5, fontWeight: 600, color: styles.fg }}>
            {label}
          </Txt>
        </Box>
      </Flx>

      {hasAnyDetails && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: 1.25,
            p: 1.25,
            borderRadius: "8px",
            background: grey[50],
            border: `1px solid ${grey[100]}`,
          }}
        >
          <FloodDetail
            label="Zone"
            value={result?.floodZone ? `Zone ${result.floodZone}` : null}
          />
          <FloodDetail label="In SFHA" value={sfhaValue} />
          <FloodDetail
            label="NFIP Community"
            value={result?.nfipCommunityName || result?.nfipCommunityId || null}
          />
          <FloodDetail label="NFIP Panel" value={result?.nfipPanelId} />
          <FloodDetail label="Panel Date" value={panelDate} />
          <FloodDetail label="Map Date" value={mapDate} />
          <FloodDetail label="Determination" value={determinationDate} />
          <FloodDetail label="Cert ID" value={result?.floodCertificationId} />
        </Box>
      )}

      {followUpMessage && (
        <Flx
          g={0.75}
          sx={{
            p: 1,
            borderRadius: "6px",
            background: styles.bg,
            border: `1px solid ${styles.border}`,
          }}
        >
          <Icon
            sx={{ fontSize: 14, color: styles.fg, mt: "2px", flexShrink: 0 }}
          />
          <Txt sx={{ fontSize: 11.5, color: styles.fg, lineHeight: 1.45 }}>
            {followUpMessage}
          </Txt>
        </Flx>
      )}

      {(pulledAt || result?.pulledBy || vendorMessages.length > 0) && (
        <Flx ac wrap g={1.25}>
          {pulledAt && (
            <Txt sx={{ fontSize: 10.5, color: grey[600] }}>
              Pulled {pulledAt}
            </Txt>
          )}
          {result?.pulledBy && (
            <Txt sx={{ fontSize: 10.5, color: grey[600] }}>
              by {result.pulledBy}
            </Txt>
          )}
          {vendorMessages.length > 0 && (
            <Tooltip title={vendorMessages.join("\n")} placement="top" arrow>
              <Txt
                sx={{
                  fontSize: 10.5,
                  color: grey[500],
                  fontStyle: "italic",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 360,
                }}
              >
                · {vendorMessages[0]}
                {vendorMessages.length > 1
                  ? ` (+${vendorMessages.length - 1})`
                  : ""}
              </Txt>
            </Tooltip>
          )}
        </Flx>
      )}
    </Flx>
  );
};

const StatItem = ({ label, value, subtitle, valueColor, mono }) => (
  <Flx column g={0.4}>
    <Txt
      sx={{
        fontSize: 10.5,
        color: grey[500],
        lineHeight: 1,
        fontWeight: 500,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontSize: 13,
        fontWeight: 600,
        color: valueColor || grey[800],
        mt: 0.25,
        fontFamily: mono ? "ui-monospace, Menlo, monospace" : undefined,
      }}
    >
      {value}
    </Txt>
    {subtitle && (
      <Txt
        sx={{
          fontSize: 10,
          color: grey[500],
          mt: 0.1,
          fontWeight: 400,
        }}
      >
        {subtitle}
      </Txt>
    )}
  </Flx>
);

PropertyCard.displayName = "PropertyCard";

export default PropertyCard;
